<template>
  <div class="risk-zones-container">
    <!-- Модальное окно с легендой (компактное) -->
    <v-dialog v-model="showLegend" max-width="650px">
      <v-card>
        <v-card-title
          style="
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 16px;
          "
        >
          <span style="font-size: 16px">📋 Легенда пунктов</span>
          <div>
            <v-btn
              small
              color="primary"
              @click="printLegend"
              style="margin-right: 8px"
              >🖨️</v-btn
            >
            <v-btn small icon @click="showLegend = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text
          style="max-height: 60vh; overflow-y: auto; padding: 10px 16px"
        >
          <!-- Красная зона -->
          <div style="margin-bottom: 10px">
            <div
              style="
                background: #ffcdd2;
                color: #b71c1c;
                padding: 6px 10px;
                border-radius: 4px;
                font-weight: bold;
                font-size: 14px;
                margin-bottom: 4px;
              "
            >
              🔴 Красная зона (Высокий риск) — пункты 1-{{
                zonesData.red.categories.length
              }}
            </div>
            <div
              v-for="(cat, idx) in zonesData.red.categories"
              :key="'red-' + idx"
              style="
                font-size: 13px;
                padding: 3px 0;
                border-bottom: 1px dotted #eee;
                line-height: 1.4;
              "
            >
              <strong>{{ idx + 1 }}</strong
              >. {{ cat.name }}
            </div>
          </div>

          <!-- Желтая зона -->
          <div style="margin-bottom: 10px">
            <div
              style="
                background: #fff9c4;
                color: #f57f17;
                padding: 6px 10px;
                border-radius: 4px;
                font-weight: bold;
                font-size: 14px;
                margin-bottom: 4px;
              "
            >
              🟡 Желтая зона (Повышенный риск) — пункты
              {{ zonesData.red.categories.length + 1 }}-{{
                zonesData.red.categories.length +
                zonesData.yellow.categories.length
              }}
            </div>
            <div
              v-for="(cat, idx) in zonesData.yellow.categories"
              :key="'yellow-' + idx"
              style="
                font-size: 13px;
                padding: 3px 0;
                border-bottom: 1px dotted #eee;
                line-height: 1.4;
              "
            >
              <strong>{{ zonesData.red.categories.length + idx + 1 }}</strong
              >. {{ cat.name }}
            </div>
          </div>

          <!-- Зеленая зона -->
          <div style="margin-bottom: 10px">
            <div
              style="
                background: #c8e6c9;
                color: #1b5e20;
                padding: 6px 10px;
                border-radius: 4px;
                font-weight: bold;
                font-size: 14px;
                margin-bottom: 4px;
              "
            >
              🟢 Зеленая зона (Умеренный риск) — пункты
              {{
                zonesData.red.categories.length +
                zonesData.yellow.categories.length +
                1
              }}-{{
                zonesData.red.categories.length +
                zonesData.yellow.categories.length +
                zonesData.green.categories.length
              }}
            </div>
            <div
              v-for="(cat, idx) in zonesData.green.categories"
              :key="'green-' + idx"
              style="
                font-size: 13px;
                padding: 3px 0;
                border-bottom: 1px dotted #eee;
                line-height: 1.4;
              "
            >
              <strong>{{
                zonesData.red.categories.length +
                zonesData.yellow.categories.length +
                idx +
                1
              }}</strong
              >. {{ cat.name }}
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <div class="page-header">
      <h2>
        Учет отдельных категорий несовершеннолетних (Мониторинг зон риска)
      </h2>
    </div>

    <div style="display: flex; align-items: center; flex-wrap: wrap">
      <div class="class-selector" v-if="userRole === 'admin'">
        <label>Класс:</label>
        <select
          class="form-control"
          v-model="selectedClassSelect"
          @change="loadStudents"
          id="selected_class"
        >
          <option value="">Все классы</option>
          <option v-for="cls in classList" :key="cls._id" :value="cls">
            {{ cls.className }}
          </option>
        </select>
      </div>

      <div class="risk-filter" v-if="studentsList.length > 0">
        <label>Показать:</label>
        <select v-model="riskFilter" class="form-control zone-filter-select">
          <option value="has_risk">⚠️ Только с признаками риска</option>
          <option value="all">📋 Все ученики</option>
          <option value="red">🔴 Только Красная зона</option>
          <option value="yellow">🟡 Только Желтая зона</option>
          <option value="green">🟢 Только Зеленая зона</option>
        </select>
        <button
          style="width: 70px"
          @click="riskFilter = 'has_risk'"
          class="clear-filter-btn"
        >
          Сбросить
        </button>
      </div>

      <button
        @click="compactMode = !compactMode"
        class="legend-btn"
        style="background: #455a64"
        v-if="studentsList.length > 0"
      >
        {{
          compactMode
            ? "📋 Показать названия"
            : "🔢 Скрыть названия (только номера)"
        }}
      </button>

      <div class="print-controls" v-if="filteredStudentsList.length > 0">
        <button @click="showLegend = true" class="legend-btn">
          📋 Легенда пунктов
        </button>
        <button @click="printTable" class="print-btn">🖨️ Печать таблицы</button>
      </div>

      <div style="margin: 10px">
        Всего учеников: <br />
        {{ filteredStudentsList.length }}
      </div>
    </div>

    <div class="students-table" v-if="filteredStudentsList.length > 0">
      <table>
        <thead>
          <tr class="main-headers">
            <th :rowspan="compactMode ? 2 : 3" class="student-col">Ученик</th>

            <!-- Динамические заголовки зон -->
            <th
              v-for="(zone, zoneKey) in zonesData"
              :key="zoneKey"
              :colspan="zone.categories.length"
              :class="['section-header', `zone-${zoneKey}`]"
            >
              {{ zone.title }}
            </th>

            <th :rowspan="compactMode ? 2 : 3" class="score-col">
              Дата обновления
            </th>
            <th :rowspan="compactMode ? 2 : 3" class="score-col">
              Комментарий
            </th>
            <th :rowspan="compactMode ? 2 : 3" class="score-col">
              Уровень риска
            </th>
          </tr>
          <!-- Строка с вертикальными названиями — только если не compactMode -->
          <tr class="vertical-headers" v-if="!compactMode">
            <template v-for="(zone, zoneKey) in zonesData">
              <th
                v-for="(category, idx) in zone.categories"
                :key="`${zoneKey}-${idx}`"
                class="vertical-text"
                :title="category.description || category.name"
                @mouseenter="highlightColumn(`${zoneKey}-${idx}`)"
                @mouseleave="resetHighlight"
              >
                <div class="vertical-content">{{ category.name }}</div>
              </th>
            </template>
          </tr>
          <tr class="vertical-headers-1">
            <!-- Динамические подзаголовки критериев -->
            <template v-for="(zone, zoneKey) in zonesData">
              <th
                v-for="(category, idx) in zone.categories"
                :key="`${zoneKey}-${idx}`"
                :title="category.description || category.name"
                @mouseenter="highlightColumn(`${zoneKey}-${idx}`)"
                @mouseleave="resetHighlight"
              >
                <div class="vertical-number">
                  {{ getGlobalIndex(zoneKey, idx) }}
                </div>
              </th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="student in filteredStudentsList"
            :key="student._id"
            class="student-row"
          >
            <td class="student-info">
              <strong
                >{{ getStudentClass(student) }} {{ student.FirstName }}
                {{ student.LastName }}
              </strong>
            </td>

            <!-- Рендерим ячейки с галочками по зонам -->
            <template v-for="(zone, zoneKey) in zonesData">
              <td
                v-for="(category, idx) in zone.categories"
                :key="`${student._id}-${zoneKey}-${idx}`"
                class="checkbox-cell"
                :class="{
                  highlighted: highlightedColumn === `${zoneKey}-${idx}`,
                }"
                @mouseenter="highlightColumn(`${zoneKey}-${idx}`)"
                @mouseleave="resetHighlight"
              >
                <div
                  class="status-icon"
                  :class="{
                    checked: isCategoryChecked(student, zoneKey, category.id),
                    unchecked: !isCategoryChecked(
                      student,
                      zoneKey,
                      category.id,
                    ),
                  }"
                  @click="toggleCategory(student, zoneKey, category.id)"
                ></div>
              </td>
            </template>

            <!-- Дата обновления -->
            <td>
              <div v-if="student.riskZones && student.riskZones.lastUpdate">
                {{
                  student.riskZones.lastUpdate
                    ? new Date(
                        student.riskZones.lastUpdate,
                      ).toLocaleDateString()
                    : ""
                }}
              </div>
            </td>

            <!-- Комментарий -->
            <td style="padding: 0; margin: 0; height: 100%">
              <textarea
                v-model="student.riskZones.notes"
                @change="updateStudentState(student)"
                style="
                  width: 100%;
                  height: 100%;
                  min-height: 60px;
                  padding: 4px 8px;
                  margin: 0;
                  font-size: 12px;
                  line-height: 1.4;
                  border: none;
                  border-radius: 0;
                  resize: vertical;
                  background: #f9f9f9;
                  font-family: inherit;
                  display: block;
                  box-sizing: border-box;
                "
                @focus="(e) => (e.target.style.background = '#fff')"
                @blur="(e) => (e.target.style.background = '#f9f9f9')"
              ></textarea>
            </td>

            <!-- Уровень риска -->
            <td class="score-cell" :class="getRiskClassByZone(student)">
              {{ getRiskZoneName(student) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="no-data">
      {{
        riskFilter === "has_risk"
          ? "Нет учеников с признаками риска (отличная новость!)"
          : riskFilter !== "all"
          ? `Нет студентов с уровнем риска "${getFilterName(riskFilter)}"`
          : "Нет данных для отображения"
      }}
    </div>

    <v-dialog v-model="loading" width="auto">
      <v-card
        height="70px"
        width="100px"
        style="display: flex; flex-direction: column; justify-content: center"
      >
        <Preloader v-if="loading" ref="Preloader" />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import TutorialDataService from "../services/TutorialDataService";
const Preloader = () => import("./Preloader.vue");

export default {
  name: "RiskZonesChecklist",
  components: {
    Preloader: Preloader,
  },
  props: {
    selectedClass: Object,
  },
  data() {
    return {
      studentsList: [],
      compactMode: false,
      showLegend: false,
      classList: [],
      selectedClassSelect: null,
      userRole: "admin",
      highlightedColumn: null,
      riskFilter: "has_risk", // 'all', 'red', 'yellow', 'green'
      loading: false,

      // Структура данных для UI (взято из Приложения.docx)
      zonesData: {
        red: {
          title: "Красная зона (Высокий риск)",
          categories: [
            {
              id: "pdn",
              name: "несовершеннолетние, состоящие на профилактическом учете в ПДН",
            },
            {
              id: "deviant",
              name: "несовершеннолетние, проявляющие признаки девиантного, деструктивного поведения, аутоагрессии",
            },
            {
              id: "informal",
              name: "несовершеннолетние, участвующие в неформальных объединениях и организациях антиобщественной направленности",
            },
            {
              id: "released",
              name: "несовершеннолетние, освобожденные из учреждений уголовно-исполнительной системы, вернувшиеся из специальных учебно-воспитательных учреждений закрытого типа",
            },
            {
              id: "shooting",
              name: "несовершеннолетние сторонники движения «скулшутинг», «колумбайн»",
            },
            {
              id: "bullying_doer",
              name: "несовершеннолетние, занимающиеся «травлей»",
            },
            { id: "substances", name: "употребление ПАВ" },
            { id: "suicidal", name: "склонность к суицидальному поведению" },
            {
              id: "admin_offense",
              name: "совершение административного правонарушения (мелкое хулиганство, распитие спиртного, появление в пьяном виде и т.д.)",
            },
            {
              id: "outcast",
              name: "несовершеннолетние, попавшие в категорию «отверженных» по итогам социометрии",
            },
            {
              id: "bullying_victim",
              name: "несовершеннолетние, подвергшиеся «травле»",
            },
          ],
        },
        yellow: {
          title: "Желтая зона (Повышенный риск)",
          categories: [
            {
              id: "absent_failing",
              name: "несовершеннолетние, непосещающие или систематически пропускающие занятия без уважительных причин, неуспевающие по учебным предметам, а также систематические опоздания на занятия без уважительных причин",
            },
            {
              id: "sop",
              name: "несовершеннолетние, находящиеся в социально-опасном положении",
            },
            {
              id: "no_citizen",
              name: "несовершеннолетние, не имеющие гражданства, проживающие в семьях беженцев, переселенцев",
            },
            {
              id: "violations",
              name: "несовершеннолетние, систематически (неоднократно в течение шести месяцев) допускающие неисполнение или нарушение устава образовательной организации, правил внутреннего распорядка и т.д.",
            },
            {
              id: "runaway",
              name: "несовершеннолетние, совершившие самовольные уходы из семей, образовательных организаций с круглосуточным пребыванием несовершеннолетних и иные",
            },
            {
              id: "shelter",
              name: "несовершеннолетние, содержащиеся в социально-реабилитационных центрах для несовершеннолетних, социальных приютах, центрах помощи детям, оставшимся без попечения родителей, специальных учебно-воспитательных и других учреждениях для несовершеннолетних, нуждающихся в социальной помощи и (или) реабилитации",
            },
            {
              id: "spt_risk",
              name: "несовершеннолетнее, попавшие в «группу риска» по результатам социально-психологического тестирования или иной системы мониторинга по представлению педагога-психолога образовательной организации",
            },
            {
              id: "sexual_violence",
              name: "несовершеннолетние, подвергшиеся сексуальному насилию",
            },
            {
              id: "domestic_violence",
              name: "несовершеннолетние, подвергшиеся жестокому обращению в семье (физическое, психологическое насилие со стороны родителей/опекунов)",
            },
            {
              id: "war_zone",
              name: "несовершеннолетние, прибывшие из зон боевых действий",
            },
            {
              id: "internet_addiction",
              name: "игровая и интернет-зависимость (компьютерные игры, азартные игры онлайн, кибербуллинг как соучастник и т.д.)",
            },
            {
              id: "parents_disabled",
              name: "несовершеннолетние, члены семей с родителями-инвалидами или родителями, имеющими психические расстройства",
            },
          ],
        },
        green: {
          title: "Зеленая зона (Умеренный риск)",
          categories: [
            {
              id: "relatives_no_custody",
              name: "несовершеннолетние, проживающие у близких родственников без оформления временной опеки",
            },
            {
              id: "incomplete",
              name: "несовершеннолетние, проживающие в неполной семье, с мачехой или отчимом",
            },
            {
              id: "poor",
              name: "несовершеннолетние из малообеспеченных семей",
            },
            {
              id: "orphan_guardian",
              name: "дети-сироты, находящиеся под опекой/воспитывающиеся в замещающих семьях",
            },
            {
              id: "state_institution",
              name: "несовершеннолетние, воспитывающиеся в государственных учреждениях",
            },
            {
              id: "foster_family",
              name: "несовершеннолетние, проживающие в приемных семьях",
            },
            {
              id: "refuse_spt",
              name: "несовершеннолетние, отказавшиеся проходить социально-психологическое тестирование и мониторинг безопасности образовательной среды",
            },
            {
              id: "home_edu",
              name: "несовершеннолетние, находящиеся на семейном обучении, самообразовании",
            },
            {
              id: "svo_parents",
              name: "несовершеннолетние, родители (законные представители) которых являются ветеранами (участниками) СВО и КТО или погибшими/пропавшими без вести на зоне проведения СВО и КТО",
            },
            {
              id: "parents_jailed",
              name: "несовершеннолетние, чьи родители находятся в местах лишения свободы или под следствием",
            },
            {
              id: "disabled_student",
              name: "несовершеннолетние с ОВЗ или инвалидностью, обучающиеся в обычном классе",
            },
            {
              id: "unemployed_parents",
              name: "несовершеннолетние, проживающие в семьях безработных родителей",
            },
            {
              id: "med_refuse",
              name: "несовершеннолетние, отказавшиеся от прохождения профилактических медицинских осмотров, либо не явившихся на профилактические медицинские осмотры",
            },
          ],
        },
      },
    };
  },

  methods: {
    // Проверка, отмечена ли категория у студента
    isCategoryChecked(student, zoneKey, categoryId) {
      if (!student.riskZones || !student.riskZones[zoneKey]) return false;
      return student.riskZones[zoneKey][categoryId] === true;
    },

    // Переключение галочки
    toggleCategory(student, zoneKey, categoryId) {
      // Инициализация объекта при необходимости
      if (!student.riskZones) {
        student.riskZones = this.getDefaultRiskZones();
      }
      if (!student.riskZones[zoneKey]) {
        student.riskZones[zoneKey] = {};
      }

      // Инвертируем значение
      student.riskZones[zoneKey][categoryId] =
        !student.riskZones[zoneKey][categoryId];

      this.updateStudentState(student);
    },

    // Определение основной зоны риска для студента
    getStudentZone(student) {
      if (!student.riskZones) return "none";

      // Приоритет: Красная > Желтая > Зеленая
      if (
        student.riskZones.red &&
        Object.values(student.riskZones.red).some((v) => v)
      ) {
        return "red";
      }
      if (
        student.riskZones.yellow &&
        Object.values(student.riskZones.yellow).some((v) => v)
      ) {
        return "yellow";
      }
      if (
        student.riskZones.green &&
        Object.values(student.riskZones.green).some((v) => v)
      ) {
        return "green";
      }
      return "none";
    },

    getRiskZoneName(student) {
      const zone = this.getStudentZone(student);
      const map = {
        red: "Высокий",
        yellow: "Средний",
        green: "Низкий",
        none: "Не определен",
      };
      return map[zone] || "Не определен";
    },

    getRiskClassByZone(student) {
      const zone = this.getStudentZone(student);
      if (zone === "red") return "risk-high";
      if (zone === "yellow") return "risk-medium";
      if (zone === "green") return "risk-low";
      return "";
    },

    getFilterName(filter) {
      const map = { red: "Красная", yellow: "Желтая", green: "Зеленая" };
      return map[filter] || filter;
    },

    getDefaultRiskZones() {
      return {
        red: {},
        yellow: {},
        green: {},
        lastUpdate: "",
        notes: "",
      };
    },

    async loadStudents() {
      try {
        let params = {};
        if (this.selectedClass.classID != "admin") {
          document.getElementById("selected_class").disabled = true;
          params.classID = this.selectedClass.classID;
        } else if (this.selectedClassSelect && this.selectedClassSelect._id) {
          params.classID = this.selectedClassSelect._id;
        }

        this.loading = true;
        const response = await TutorialDataService.findStudentByClassID(params);
        const studentsObject = response.data || response;
        const studentsArray = Object.values(studentsObject);

        this.studentsList = studentsArray.map((student) => {
          if (!student.riskZones) {
            student.riskZones = this.getDefaultRiskZones();
          }
          return student;
        });
        this.loading = false;
      } catch (error) {
        console.error("Ошибка загрузки студентов:", error);
        this.studentsList = [];
        this.loading = false;
      }
    },

    async loadClassList() {
      try {
        const response = await TutorialDataService.getAllCLass();
        this.classList = response.data;
      } catch (error) {
        console.error("Ошибка загрузки классов:", error);
      }
    },

    async updateStudentState(student) {
      try {
        student.riskZones.lastUpdate = new Date();
        this.loading = true;
        await TutorialDataService.updateStudentRiskZones(student._id, {
          riskZones: student.riskZones,
        });
        this.loading = false;
      } catch (error) {
        console.error("Ошибка сохранения:", error);
        alert("Ошибка сохранения!");
      }
    },

    getStudentClass(student) {
      const classInfo = this.classList.find(
        (cls) => cls._id === student.classID,
      );
      return classInfo ? classInfo.className : "Не указан";
    },

    highlightColumn(columnId) {
      this.highlightedColumn = columnId;
    },

    resetHighlight() {
      this.highlightedColumn = null;
    },
    getGlobalIndex(zoneKey, idx) {
      const zoneOrder = ["red", "yellow", "green"];
      let globalIdx = 1;

      for (let zone of zoneOrder) {
        if (zone === zoneKey) {
          return globalIdx + idx;
        }
        globalIdx += this.zonesData[zone].categories.length;
      }
      return globalIdx + idx;
    },
    printLegend() {
      const printWindow = window.open("", "_blank");

      let redItems = "";
      this.zonesData.red.categories.forEach((cat, idx) => {
        redItems += `<div style="font-size: 12px; padding: 2px 0; line-height: 1.4;"><strong>${
          idx + 1
        }.</strong> ${cat.name}</div>`;
      });

      let yellowItems = "";
      const yellowStart = this.zonesData.red.categories.length + 1;
      this.zonesData.yellow.categories.forEach((cat, idx) => {
        yellowItems += `<div style="font-size: 12px; padding: 2px 0; line-height: 1.4;"><strong>${
          yellowStart + idx
        }.</strong> ${cat.name}</div>`;
      });

      let greenItems = "";
      const greenStart =
        this.zonesData.red.categories.length +
        this.zonesData.yellow.categories.length +
        1;
      this.zonesData.green.categories.forEach((cat, idx) => {
        greenItems += `<div style="font-size: 12px; padding: 2px 0; line-height: 1.4;"><strong>${
          greenStart + idx
        }.</strong> ${cat.name}</div>`;
      });

      const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Легенда пунктов</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { 
          font-family: Arial, sans-serif; 
          margin: 8mm; 
          font-size: 12px; 
        }
        h2 { 
          text-align: center; 
          margin-bottom: 10px; 
          font-size: 15px; 
        }
        .zone-title { 
          margin: 6px 0 3px 0; 
          padding: 4px 8px; 
          border-radius: 3px; 
          font-size: 13px; 
          font-weight: bold; 
        }
        .zone-red { background: #ffcdd2; color: #b71c1c; }
        .zone-yellow { background: #fff9c4; color: #f57f17; }
        .zone-green { background: #c8e6c9; color: #1b5e20; }
        .item { font-size: 11.5px; padding: 1.5px 0; line-height: 1.35; }
        
        @media print {
          body { margin: 6mm; }
          @page { size: A4; margin: 6mm; }
        }
      </style>
    </head>
    <body>
      <h2>📋 Легенда пунктов мониторинга зон риска</h2>
      
      <div class="zone-title zone-red">🔴 Красная зона (пункты 1-${
        this.zonesData.red.categories.length
      })</div>
      ${redItems}
      
      <div class="zone-title zone-yellow">🟡 Желтая зона (пункты ${yellowStart}-${
        yellowStart + this.zonesData.yellow.categories.length - 1
      })</div>
      ${yellowItems}
      
      <div class="zone-title zone-green">🟢 Зеленая зона (пункты ${greenStart}-${
        greenStart + this.zonesData.green.categories.length - 1
      })</div>
      ${greenItems}
    </body>
    </html>
  `;

      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 300);
    },

    printTable() {
      const printWindow = window.open("", "_blank");
      const currentDate = new Date().toLocaleDateString("ru-RU");

      // Генерируем строки таблицы
      let tableRows = "";
      this.filteredStudentsList.forEach((student) => {
        let cells = "";

        for (let zoneKey in this.zonesData) {
          this.zonesData[zoneKey].categories.forEach((cat) => {
            const checked = this.isCategoryChecked(student, zoneKey, cat.id);
            cells += `<td style="border: 1px solid #000; text-align: center; vertical-align: middle; padding: 2px;">${
              checked ? "V" : ""
            }</td>`;
          });
        }

        const dateStr = student.riskZones?.lastUpdate
          ? new Date(student.riskZones.lastUpdate).toLocaleDateString()
          : "";
        const notes = (student.riskZones?.notes || "").substring(0, 50);

        const riskLevel = this.getRiskZoneName(student);
        const riskSymbol =
          riskLevel === "Высокий"
            ? "!!!"
            : riskLevel === "Средний"
            ? "!!"
            : riskLevel === "Низкий"
            ? "!"
            : "";

        tableRows += `
      <tr>
        <td style="border: 1px solid #000; padding: 4px; white-space: nowrap; background: #fff;">
          <strong>${this.getStudentClass(student)} ${student.FirstName} ${
          student.LastName
        }</strong>
        </td>
        ${cells}
        <td style="border: 1px solid #000; text-align: center; padding: 2px; font-size: 10px; white-space: nowrap;">${dateStr}</td>
        <td style="border: 1px solid #000; padding: 2px 4px; font-size: 9px; max-width: 120px; overflow: hidden;">${notes}</td>
        <td style="border: 1px solid #000; text-align: center; padding: 2px; font-weight: bold; white-space: nowrap;">${riskSymbol}</td>
      </tr>
    `;
      });

      // ===== ЗАГОЛОВКИ =====
      let headerRow1 = `<th rowspan="3" style="border: 1px solid #000; padding: 4px; min-width: 120px; background: #fff;">Ученик</th>`;
      let headerRow2 = "";
      let headerRow3 = "";

      for (let zoneKey in this.zonesData) {
        const zone = this.zonesData[zoneKey];
        const zoneShortName =
          zoneKey === "red"
            ? "Красная"
            : zoneKey === "yellow"
            ? "Желтая"
            : "Зеленая";

        headerRow1 += `<th colspan="${zone.categories.length}" style="border: 1px solid #000; padding: 6px; background-color: ${zoneKey}; font-size: 11px;">${zoneShortName} зона</th>`;

        zone.categories.forEach((cat, idx) => {
          const globalNumber = this.getGlobalIndex(zoneKey, idx);

          headerRow2 += `
        <th class="vertical-th" style="border: 1px solid #000; width: 30px; vertical-align: bottom; padding: 2px 1px; background: #fff;">
          <div class="vertical-text-print">
            ${cat.name}
          </div>
        </th>
      `;

          headerRow3 += `
        <th class="number-th" style="border: 1px solid #000; width: 30px; padding: 2px 1px; background: #f0f0f0; font-size: 11px; font-weight: bold; color: #555; text-align: center;">
          ${globalNumber}
        </th>
      `;
        });
      }

      headerRow1 += `
    <th rowspan="3" style="border: 1px solid #000; padding: 4px; min-width: 70px; background: #fff;">Дата</th>
    <th rowspan="3" style="border: 1px solid #000; padding: 4px; min-width: 100px; background: #fff;">Комментарий</th>
    <th rowspan="3" style="border: 1px solid #000; padding: 4px; min-width: 45px; background: #fff;">Риск</th>
  `;

      const tableHtml = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Мониторинг зон риска</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body {
          font-family: 'Arial', sans-serif;
          margin: 8mm;
          font-size: 10px;
          background: #fff;
        }
        .print-header { text-align: center; margin-bottom: 10px; }
        .print-header h2 { margin: 0 0 3px 0; font-size: 14px; font-weight: bold; }
        .print-header div { font-size: 11px; }
        .print-date { text-align: right; margin-top: 15px; font-style: italic; font-size: 10px; }

        table {
          width: 100%;
          border-collapse: collapse;
          border: 2px solid #000;
          background: #fff;
        }
        th, td { border: 1px solid #000; vertical-align: middle; }
        th { font-weight: bold; text-align: center; background: #fff; }

        /* Вертикальный текст в заголовке */
        .vertical-text-print {
          writing-mode: vertical-rl;
          transform: rotate(180deg);
          text-orientation: mixed;
          white-space: normal;
          word-wrap: break-word;
          line-height: 1.2;
          font-size: 8px;
          max-height: 170px;
          overflow: hidden;
          margin: 0 auto;
          text-align: left;
        }

        .legend {
          margin-top: 15px;
          padding: 8px;
          border: 1px solid #000;
          font-size: 10px;
        }
        .legend-item { display: inline-block; margin-right: 15px; }

        /* ===== ГОРИЗОНТАЛЬНАЯ ОРИЕНТАЦИЯ A4 ===== */
        @page {
          size: A4 landscape;
          margin: 6mm;
        }

        @media print {
          body {
            margin: 0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          table { page-break-inside: auto; }

          thead {
            display: table-header-group !important; /* ← сквозная шапка */
          }
          tbody { display: table-row-group; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          th, td { border: 1px solid #000 !important; }

          /* В landscape лист ниже — уменьшаем высоту вертикальной строки */
          .vertical-text-print {
            max-height: 150px !important;
            font-size: 7.5px !important;
          }

          .vertical-th {
            height: 160px !important;
          }

          .number-th {
            height: 18px !important;
          }

          .print-header {
            margin-bottom: 6px;
          }
          .print-header h2 {
            font-size: 13px;
          }
          .print-header div {
            font-size: 10px;
          }
        }
      </style>
    </head>
    <body>
      <div class="print-header">
        <h2>УЧЕТ ОТДЕЛЬНЫХ КАТЕГОРИЙ НЕСОВЕРШЕННОЛЕТНИХ</h2>
        <div>Мониторинг зон риска</div>
      </div>

      <table>
        <thead>
          <tr>${headerRow1}</tr>
          <tr>${headerRow2}</tr>
          <tr>${headerRow3}</tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>

      <div class="legend">
        <span class="legend-item"><strong>Обозначения риска:</strong></span>
        <span class="legend-item">!!! — Высокий риск (Красная зона)</span>
        <span class="legend-item">!! — Средний риск (Желтая зона)</span>
        <span class="legend-item">! — Низкий риск (Зеленая зона)</span>
        <span class="legend-item">V — Признак присутствует</span>
      </div>
      <br>
      <div>Классный руководитель: ______________________________________</div>

      <div class="print-date">
        По состоянию на ${currentDate}
      </div>
    </body>
    </html>
  `;

      printWindow.document.write(tableHtml);
      printWindow.document.close();

      printWindow.onload = function () {
        printWindow.focus();
        printWindow.print();
      };

      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 300);
    },
  },

  computed: {
    filteredStudentsList() {
      let filtered = this.studentsList;
      // Новая логика фильтрации
      if (this.riskFilter === "has_risk") {
        // Показываем только тех, у кого есть хоть одна галочка
        filtered = filtered.filter((student) => {
          return this.getStudentZone(student) !== "none";
        });
      } else if (this.riskFilter !== "all") {
        // Фильтр по конкретной зоне
        filtered = filtered.filter((student) => {
          return this.getStudentZone(student) === this.riskFilter;
        });
      }

      return filtered.sort((a, b) => {
        const classA = this.getStudentClass(a);
        const classB = this.getStudentClass(b);
        if (classA < classB) return -1;
        if (classA > classB) return 1;
        const firstNameA = a.FirstName || "";
        const firstNameB = b.FirstName || "";
        return firstNameA.localeCompare(firstNameB);
      });
    },
  },

  async mounted() {
    await this.loadClassList();
    if (this.selectedClass.classID != "admin") {
      document.getElementById("selected_class").disabled = true;
      this.loadStudents();
    }
  },
};
</script>

<style scoped>
/* Берем 95% стилей из EmotionalStateChecklist.vue */
/* Добавляем только специфичные для зон цвета */

.zone-red {
  background: #ffcdd2 !important;
  color: #b71c1c;
}
.zone-yellow {
  background: #fff9c4 !important;
  color: #f57f17;
}
.zone-green {
  background: #c8e6c9 !important;
  color: #1b5e20;
}

.zone-filter-select {
  width: auto;
  min-width: 180px;
  padding: 6px 10px;
  border-radius: 4px;
  border: 1px solid #ccc;
}

/* Остальные стили идентичны твоему файлу (padding, sticky, hover, status-icon) */
/* ... сюда копируется весь блок <style> из EmotionalStateChecklist.vue ... */
/* Для краткости в ответе они опущены, но в коде компонента их нужно вставить обязательно */

.emotional-state-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}

.class-selector {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 150px;
}

.students-table {
  overflow-x: auto; /* ← Включает горизонтальный скролл */
  overflow-y: auto; /* ← Вертикальный скролл уже был */
  background: white;
  max-height: 72vh;
  max-width: 99vw;
  padding: 5px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
}

/* Основные стили таблицы */
table {
  min-width: 900px;
  border-collapse: collapse;
  font-size: 13px;
}

/* Контейнер для sticky заголовков */
thead {
  position: sticky;
  top: 0;
  z-index: 100; /* ← Должен быть ниже чем у student-col (102) и student-info (99) */
  background: white;
}

th,
td {
  border: 1px solid #e0e0e0;
  padding: 8px 6px;
  position: relative;
}

/* Сохраняем подсветку строк */
.student-row:hover td:not(.student-info) {
  background: #b8deff !important;
}
.student-row:hover td:not(.student-info):not(.student-col) {
  background: #b8deff !important;
}

/* Подсветка строк */
.student-row:hover {
  background: #b8deff !important;
}

/* Заголовки */
.main-headers th {
  background: #f5f5f5;
  font-weight: 600;
  font-size: 12px;
  position: sticky;
  top: 0;
  z-index: 101;
}

.vertical-headers th {
  background: #f8f9fa;
  font-weight: 500;
  height: 240px; /* ← Увеличил высоту */
  min-width: 56px; /* ← Фиксированная ширина */
  width: 56px; /* ← Одинаковая для всех */
  max-width: 56px;
  vertical-align: top; /* ← Текст прижат к низу */
  padding: 6px 2px;
  position: sticky;
  top: 38px;
  z-index: 98;
}

.vertical-headers-1 th {
  background: #f8f9fa;
  font-weight: 500;
  height: 20px; /* ← Увеличил высоту */
  min-width: 56px; /* ← Фиксированная ширина */
  width: 56px; /* ← Одинаковая для всех */
  max-width: 56px;
  vertical-align: top; /* ← Текст прижат к низу */
  padding: 6px 2px;
  position: sticky;
  top: 38px;
  z-index: 98;
}

.vertical-text {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  text-orientation: mixed;
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.3;
  padding: 8px 4px;
  font-size: 11px;
  cursor: help;
}

.vertical-content {
  display: inline-block;
  overflow: hidden;
}

.student-col {
  background: #e3f2fd;
  min-width: 140px;
  max-width: 160px;

  /* Вот это делает колонку приклеенной */
  position: sticky;
  left: 0;
  z-index: 102; /* ← Выше чем у thead (z-index: 100) */

  /* Чтобы фон не просвечивал при скролле */
  background-color: #e3f2fd;
  box-shadow: 2px 0 5px -2px rgba(0, 0, 0, 0.1); /* ← Тень справа для эффекта отделения */
}

.score-col {
  background: #f5f5f5;
  min-width: 60px;
  font-weight: 600;
}

.student-info {
  text-align: left;
  font-size: 15px;
  line-height: 1.3;

  /* Тоже фиксируем, чтобы не отлипала */
  position: sticky;
  left: 0;
  z-index: 99; /* ← Ниже чем заголовок, но выше чем обычные ячейки */
  background-color: #ffffff; /* ← Важно! Иначе будет прозрачной */
}

.checkbox-cell {
  min-width: 38px;
  width: 38px;
  max-width: 38px;
  cursor: pointer;
  text-align: center;
}

.score-cell {
  font-weight: bold;
  font-size: 13px;
}

.risk-low {
  background: #e8f5e8;
  color: #2e7d32;
}
.risk-medium {
  background: #fff3e0;
  color: #ef6c00;
}
.risk-high {
  background: #ffebee;
  color: #c62828;
}

/* Стили для статусных иконок */
.status-icon {
  display: inline-flex;
  width: 42px;
  height: 42px;
  border-radius: 40%;
  justify-content: center;
  align-items: center;
  transition: all 0.2s;
  cursor: pointer;
  margin: 0 auto;
}

.status-icon.unchecked {
  background-color: #dfdfdf;
}

.status-icon.unchecked::after {
  content: "✕";
  color: white;
  font-weight: bold;
  font-size: 16px;
  text-shadow: 0 0 1px #999;
}

.status-icon.checked {
  background-color: #ffc5ce;
}

.status-icon.checked::after {
  content: "✓";
  color: #c62828;
  font-weight: bold;
  font-size: 16px;
  border: #c62828 1px;
}

.no-data {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
}

/* Фикс для sticky в Firefox */
@supports (-moz-appearance: none) {
  thead {
    background: white;
  }
}

/* Подсветка колонок */
/* Исправленная подсветка колонок */
td {
  position: relative;
  overflow: hidden;
}

td:hover:after {
  pointer-events: none;
  position: initial;
  z-index: 1000;
  top: 0;
  left: 0;
  width: 100%;
  height: 1000px;
  content: "";
  background-color: rgba(106, 241, 246, 0.678);
}

/* Отключаем подсветку для ячеек с учениками */
.student-info:hover::before {
  display: none;
}

.vertical-number {
  font-size: 14px;
  font-weight: bold;
  color: #555;
  text-align: center;
  margin-bottom: 4px;
  transform: none;
  width: 100%;
  border-bottom: 1px solid #ddd;
  padding-bottom: 2px;
}
/* Подсветка столбцов */
.checkbox-cell.highlighted,
.score-cell.highlighted {
  background-color: rgba(106, 241, 246, 0.4) !important;
}

/* Убедитесь, что эти стили переопределяют существующие */
.student-row td.checkbox-cell.highlighted,
.student-row td.score-cell.highlighted {
  background-color: rgba(106, 241, 246, 0.4) !important;
}

/* Убираем старый способ подсветки через псевдоэлементы */
td:hover:after {
  display: none;
}

/* Добавляем подсветку через классы на основе номера столбца */
:root {
  --highlighted-column: 0;
}

/* Динамическая подсветка столбцов */
.checkbox-cell:nth-child(1).highlighted,
.score-cell:nth-child(18).highlighted {
  /* 18 потому что первый столбец ученик + 17 столбцов */
  background-color: rgba(106, 241, 246, 0.4) !important;
}

.risk-filter {
  margin: 15px 0;
  padding: 12px 15px;
  background: #f8f9fa;
  border-radius: 8px;
  display: grid;
  grid-template-columns: 200px 1fr 150px 80px; /* Фиксированные колонки */
  gap: 10px;
  align-items: center;
  max-width: 500px;
}

.risk-filter label {
  font-weight: 500;
  white-space: nowrap;
}

.risk-value {
  font-weight: bold;
  color: #1976d2;
  text-align: center;
  font-family: "Courier New", monospace;
}

.clear-filter-btn {
  background: #b4b4b4;
  color: white;
  border: none;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  white-space: nowrap;
}
.clear-filter-btn:hover {
  background: #a0a0a0;
}

.print-controls {
  margin: 15px 0;
  text-align: right;
}

.print-btn {
  background: #1976d2;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.3s;
}

.print-btn:hover {
  background: #1565c0;
}

.zone-red-text {
  background: #ffcdd2;
  color: #b71c1c;
}
.zone-yellow-text {
  background: #fff9c4;
  color: #f57f17;
}
.zone-green-text {
  background: #c8e6c9;
  color: #1b5e20;
}

.legend-btn {
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.3s;
  margin-right: 10px;
}
.legend-btn:hover {
  background: #5a6268;
}

/* Скрываем элементы при печати на основной странице */
@media print {
  .risk-filter,
  .print-controls,
  .page-header,
  .class-selector {
    display: none !important;
  }
}
</style>
